import { iString, type LocaleId } from '@strings';
import { CadetLexer } from './CadetLexer';
import * as TT from './CadetTokens';
import { canonicalizeLocale, isWellFormedLocale } from './i18nLocale';

/**
 * Parses a standalone IString literal using the CADET-DSL parsing machinery.
 *
 * @param text - The IString literal text to parse, e.g., `'Red'~en-US 'Rojo'~es-ES`
 * @returns An IString instance with the parsed locale variants
 *
 * @example
 * ```typescript
 * const result = parseIStringLiteral(`'Hello'~en-US 'Hola'~es-ES`);
 * console.log(result.get('en-US')); // 'Hello'
 * console.log(result.get('es-ES')); // 'Hola'
 * ```
 *
 * @example
 * ```typescript
 * // Single variant (plain string)
 * const simple = parseIStringLiteral(`'Hello'`);
 * console.log(simple.get('en-US')); // 'Hello'
 * ```
 *
 * @throws {Error} When the input cannot be parsed as a valid IString literal
 */
export function parseIStringLiteral(text: string): ReturnType<typeof iString> {
  try {
    // Create a lexer with just the IString literal (no prologue needed for standalone parsing)
    const lexer = new CadetLexer(`%CADET-1.0\n${text}`);

    // Skip the prologue token
    if (lexer.tokens[0]?.type !== TT.PROLOGUE) {
      throw new Error('Invalid CADET prologue');
    }

    let pos = 1; // Start after prologue

    // Helper to get current token
    const current = () => {
      if (pos >= lexer.tokens.length) return undefined;
      return lexer.tokens[pos];
    };

    // Helper to advance position
    const consume = () => {
      const token = lexer.tokens[pos];
      pos++;
      return token;
    };

    // Helper to consume breaks
    const consumeBreaks = () => {
      while (pos < lexer.tokens.length && current()?.type === TT.BREAK) {
        pos++;
      }
    };

    // Start parsing
    consumeBreaks();

    // Parse first string
    const firstToken = current();
    if (!firstToken || firstToken.type !== TT.STRING) {
      throw new Error('Expected string literal');
    }
    const firstString = consume();
    const firstValue = firstString.value as string;

    consumeBreaks();

    // Check if there's a tilde (indicating locale variants)
    const tildeToken = current();
    if (!tildeToken || tildeToken.type !== TT.TILDE) {
      // Plain string, create IString with default locale
      return iString(firstValue);
    }

    // Consume the tilde
    consume();

    // Parse first locale
    const firstLocaleToken = current();
    if (!firstLocaleToken || firstLocaleToken.type !== TT.LOCALE) {
      throw new Error('Expected locale after ~');
    }
    const firstLocaleRaw = consume().text;

    if (!isWellFormedLocale(firstLocaleRaw)) {
      throw new Error(`Invalid locale tag: ${firstLocaleRaw}`);
    }

    const firstLocale = canonicalizeLocale(firstLocaleRaw);
    const variants: Record<string, string> = { [firstLocale]: firstValue };

    // Parse additional segments
    while (pos < lexer.tokens.length) {
      consumeBreaks();

      // Check for next string
      const segStringToken = current();
      if (!segStringToken || segStringToken.type !== TT.STRING) {
        break; // End of IString
      }
      const segString = consume();
      const segValue = segString.value as string;

      consumeBreaks();

      // Expect tilde
      const segTildeToken = current();
      if (!segTildeToken || segTildeToken.type !== TT.TILDE) {
        throw new Error('Expected ~locale after IString segment');
      }
      consume(); // consume tilde

      // Expect locale
      const segLocaleToken = current();
      if (!segLocaleToken || segLocaleToken.type !== TT.LOCALE) {
        throw new Error('Expected locale after ~');
      }
      const segLocaleRaw = consume().text;

      if (!isWellFormedLocale(segLocaleRaw)) {
        throw new Error(`Invalid locale tag: ${segLocaleRaw}`);
      }

      const segLocale = canonicalizeLocale(segLocaleRaw);

      if (Object.prototype.hasOwnProperty.call(variants, segLocale)) {
        throw new Error(`Duplicate locale variant: ${segLocale}`);
      }

      variants[segLocale] = segValue;
    }

    // Create IString from variants
    const entries = Object.entries(variants);
    const map = new Map(entries.map(([locale, value]) => [locale as LocaleId, value]));

    return iString(map);
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to parse IString literal: ${error.message}`);
    }
    throw new Error('Failed to parse IString literal: Unknown error');
  }
}
